import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type RoomLayoutApiResponse,
  type RoomLayoutSpotTypeItem,
  type RoomLayoutError,
  RoomLayoutErrorCode,
  type RoomLayoutSpot,
  type UseRoomLayoutMapResult,
  SpotStatus
} from './RoomLayoutMap.types';
import { hasSpotTypeName, hasSpotTypePrefix } from './spotTypeGuards';
import { deriveSpotKeyFromApiSpot } from './spotKey';

type BackendRoomLayoutSpot = {
  spot_id?: string | number;
  id?: string | number;
  spot_name: string;
  spot_type_id: string;
  spot_type_name?: string;
  spot_type_prefix?: string;
  status: string;
  column: number;
  row: number;
};

type BackendSpotTypeItem = {
  name: string;
  prefix: string;
};

type BackendRoomLayoutResponse = {
  /** GET /v3.0/room-layouts/:id uses "id"; GET /v3.0/time-slots/:id/room-layouts uses "_id". */
  id?: string;
  _id?: string;
  name: string;
  columns: number;
  rows: number;
  spots: BackendRoomLayoutSpot[];
  total_available_spots: number;
  total_bookable_spots: number;
  active: boolean;
  /** API key may be "equipment"; we expose as spot_types. */
  equipment?: BackendSpotTypeItem[];
  created?: string | null;
  created_by_user_id?: string | null;
  modified?: string | null;
  modified_by_user_id?: string | null;
  location_id?: string | null;
};

const SPOT_STATUS_VALUES = new Set<string>(Object.values(SpotStatus));

/**
 * Parse and normalise backend spot status to SpotStatus.
 * API may return 'BOOKED' (time-slot API); we map it to BLOCKED for UI.
 * Throws on invalid input. Use this instead of a type guard so we never narrow
 * to a union that includes 'BOOKED', preserving exhaustiveness on SpotStatus.
 */
function parseSpotStatus(value: unknown): SpotStatus {
  if (typeof value !== 'string') {
    throw new Error(`Invalid spot status: ${String(value)}`);
  }
  if (value === 'BOOKED') return SpotStatus.BLOCKED;
  if (SPOT_STATUS_VALUES.has(value)) return value as SpotStatus;
  throw new Error(`Invalid spot status: ${value}`);
}

function mapSpot(spot: BackendRoomLayoutSpot, index: number): RoomLayoutSpot {
  if (typeof spot.spot_name !== 'string') throw new Error('Invalid spot: spot_name must be string');
  if (typeof spot.spot_type_id !== 'string')
    throw new Error('Invalid spot: spot_type_id must be string');
  if (typeof spot.column !== 'number') throw new Error('Invalid spot: column must be number');
  if (typeof spot.row !== 'number') throw new Error('Invalid spot: row must be number');
  const spotName = deriveSpotKeyFromApiSpot(spot, index);
  return {
    spotName,
    spot_name: spot.spot_name,
    spot_type_id: spot.spot_type_id,
    ...(hasSpotTypeName(spot) && { spot_type_name: spot.spot_type_name }),
    ...(hasSpotTypePrefix(spot) && { spot_type_prefix: spot.spot_type_prefix }),
    status: parseSpotStatus(spot.status),
    column: spot.column,
    row: spot.row
  };
}

function assertString(value: unknown, field: string): asserts value is string {
  if (typeof value !== 'string') throw new Error(`Invalid layout: ${field} must be string`);
}

function assertNumber(value: unknown, field: string): asserts value is number {
  if (typeof value !== 'number') throw new Error(`Invalid layout: ${field} must be number`);
}

function assertBoolean(value: unknown, field: string): asserts value is boolean {
  if (typeof value !== 'boolean') throw new Error(`Invalid layout: ${field} must be boolean`);
}

/**
 * Time-slot `GET .../time-slots/:id/room-layouts` can omit or null audit fields that exist on
 * full `room_layouts` documents. The grid only needs spots geometry + status; metadata becomes ''.
 */
function layoutMetadataString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function mapResponse(response: BackendRoomLayoutResponse): RoomLayoutApiResponse {
  const id = response.id ?? response._id;
  assertString(id, 'id');
  assertString(response.name, 'name');
  assertNumber(response.columns, 'columns');
  assertNumber(response.rows, 'rows');
  assertNumber(response.total_available_spots, 'total_available_spots');
  assertNumber(response.total_bookable_spots, 'total_bookable_spots');
  assertBoolean(response.active, 'active');
  const locationId = layoutMetadataString(response.location_id);
  const created = layoutMetadataString(response.created);
  const createdByUserId = layoutMetadataString(response.created_by_user_id);
  const modified = layoutMetadataString(response.modified);
  const modifiedByUserId = layoutMetadataString(response.modified_by_user_id);

  if (!Array.isArray(response.spots)) throw new Error('Invalid layout: spots must be array');
  const spotTypes = mapSpotTypes(response.equipment);
  // Canonical place: empty grid cells are dropped; `data.spots` never includes EMPTY.
  const spots = response.spots.map(mapSpot).filter((s) => s.status !== SpotStatus.EMPTY);
  return {
    id,
    location_id: locationId,
    name: response.name,
    columns: response.columns,
    rows: response.rows,
    spots,
    total_available_spots: response.total_available_spots,
    total_bookable_spots: response.total_bookable_spots,
    active: response.active,
    ...(spotTypes.length > 0 && { spot_types: spotTypes }),
    created,
    modified,
    created_by_user_id: createdByUserId,
    modified_by_user_id: modifiedByUserId
  };
}

function mapSpotTypes(raw: unknown): RoomLayoutSpotTypeItem[] {
  if (!Array.isArray(raw)) return [];
  const result: RoomLayoutSpotTypeItem[] = [];
  for (const item of raw) {
    if (
      item !== null &&
      typeof item === 'object' &&
      typeof (item as { name?: unknown }).name === 'string' &&
      typeof (item as { prefix?: unknown }).prefix === 'string'
    ) {
      const name = (item as { name: string }).name.trim();
      const prefix = (item as { prefix: string }).prefix.trim();
      if (name !== '' && prefix !== '') result.push({ name, prefix });
    }
  }
  return result;
}

const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  return 'name' in error && (error as { name?: string }).name === 'AbortError';
};

function isApiErrorBody(value: unknown): value is { message: string; code?: string } {
  return (
    typeof value === 'object' &&
    value !== null &&
    'message' in value &&
    typeof (value as { message: unknown }).message === 'string'
  );
}

async function errorFromResponse(response: Response): Promise<RoomLayoutError> {
  try {
    const body = (await response.json()) as unknown;
    if (isApiErrorBody(body)) {
      const knownCode = Object.values(RoomLayoutErrorCode).find((c) => c === body.code);
      if (knownCode) return { code: knownCode, message: body.message };
      return { code: RoomLayoutErrorCode.CLIENT_ERROR, message: body.message };
    }
  } catch {
    // response body is not valid JSON or is empty
  }
  return errorFromStatus(response.status);
}

function errorFromStatus(status: number): RoomLayoutError {
  if (status === 404) return { code: RoomLayoutErrorCode.NOT_FOUND };
  if (status >= 500 && status < 600) return { code: RoomLayoutErrorCode.SERVER_ERROR };
  return { code: RoomLayoutErrorCode.CLIENT_ERROR, message: String(status) };
}

const LAYOUT_REQUEST_TIMEOUT_MS = 12_000;

/** Detect network/generic errors without relying on message strings (they can be localised). */
function isNetworkOrGenericError(err: unknown): boolean {
  if (!(err instanceof Error)) return true;
  // In browsers, "Failed to fetch" is a TypeError; avoid matching err.message for i18n safety.
  if (err.name === 'TypeError') return true;
  return false;
}

function errorFromCaught(err: unknown): RoomLayoutError {
  if (isAbortError(err)) {
    return { code: RoomLayoutErrorCode.CONNECTION_ERROR };
  }
  const code = isNetworkOrGenericError(err)
    ? RoomLayoutErrorCode.CONNECTION_ERROR
    : RoomLayoutErrorCode.UNKNOWN;
  const message = err instanceof Error ? err.message : undefined;
  return message !== undefined ? { code, message } : { code };
}

export function useRoomLayoutMap(
  layoutId: string,
  envUrl: string,
  userToken: string,
  callbacks?: {
    onSuccess?: (data: RoomLayoutApiResponse) => void;
    onError?: (error: RoomLayoutError) => void;
    /** Optional: for dev/test. Minimum ms to keep loading before resolving (success path only). */
    loadingMinimumMs?: number;
  },
  /** When set, fetches GET /v3.0/time-slots/{timeSlotId}/room-layouts (layout enriched with current bookings). Omit to use GET /v3.0/room-layouts/{layoutId}. */
  timeSlotId?: string
): UseRoomLayoutMapResult {
  const [data, setData] = useState<RoomLayoutApiResponse | null>(null);
  // Defensive checks when callers pass undefined (e.g. before auth/layout ready); signature stays string like envUrl.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- runtime guard for undefined from callers
  const hasToken = userToken !== undefined && userToken !== '';
  // Align with effect guard: we have a resource when either identifier is valid (inclusive OR).
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- runtime guard for undefined from callers
  const hasLayout = layoutId !== undefined;
  const hasTimeSlot = timeSlotId !== undefined && timeSlotId !== '';
  const hasResource = hasLayout || hasTimeSlot;
  const [isLoading, setIsLoading] = useState(hasResource && hasToken);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<RoomLayoutError | null>(null);
  /** Set synchronously whenever we set error state so timer callbacks see it before next render. */
  const isErrorRef = useRef(false);

  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;

  const loadLayoutRef = useRef<(() => void) | null>(null);
  /** AbortController for the in-flight request; abort before starting a new load so only the latest request updates state. */
  const currentRequestControllerRef = useRef<AbortController | null>(null);

  /** Returns true if this request is still the current one (not superseded by refetch). */
  const isCurrentRequest = useCallback((controller: AbortController) => {
    return currentRequestControllerRef.current === controller;
  }, []);

  const runLoad = useCallback(
    async (parentController: AbortController) => {
      const signal = parentController.signal;
      const startTime = performance.now();
      setIsLoading(true);
      isErrorRef.current = false;
      setIsError(false);
      setError(null);

      const controller = new AbortController();
      let timedOut = false as boolean;
      let timeoutId = null as ReturnType<typeof setTimeout> | null;
      const timeoutPromise = new Promise<Response>((_, reject) => {
        timeoutId = setTimeout(() => {
          timedOut = true;
          controller.abort();
          reject(new Error('Request timeout'));
        }, LAYOUT_REQUEST_TIMEOUT_MS);
      });
      const onParentAbort = () => {
        controller.abort();
      };
      signal.addEventListener('abort', onParentAbort);

      try {
        const base = envUrl.replace(/\/$/, '');
        const url =
          timeSlotId && timeSlotId !== ''
            ? `${base}/v3.0/time-slots/${timeSlotId}/room-layouts`
            : `${base}/v3.0/room-layouts/${layoutId}`;
        const headers: HeadersInit = {};
        headers['Authorization'] = `Bearer ${userToken}`;

        const fetchPromise = fetch(url, {
          signal: controller.signal,
          headers
        });
        // When timeout wins the race, controller.abort() makes fetch reject with AbortError;
        // suppress that expected rejection to avoid unhandledrejection / yellow-box / monitoring noise.
        fetchPromise.catch(() => {
          /* intentional no-op */
        });
        const response = await Promise.race([fetchPromise, timeoutPromise]);
        // Fetch won the race: stop the timer now so a slow JSON parse or the
        // loadingMinimumMs wait can't flip `timedOut` (and abort the controller)
        // on an already-successful response.
        if (timeoutId !== null) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }

        if (!isCurrentRequest(parentController)) return;

        if (!response.ok) {
          const err = await errorFromResponse(response);
          if (!isCurrentRequest(parentController)) return;
          setError(err);
          isErrorRef.current = true;
          setIsError(true);
          callbacksRef.current?.onError?.(err);
          return;
        }

        const raw = (await response.json()) as BackendRoomLayoutResponse;
        const mapped = mapResponse(raw);

        if (!mapped.active) {
          throw new Error(`Layout "${mapped.name}" is not active`);
        }

        const minMs = callbacksRef.current?.loadingMinimumMs;
        if (minMs !== undefined && minMs > 0) {
          const elapsed = performance.now() - startTime;
          const wait = minMs - elapsed;
          if (wait > 0) await new Promise((r) => setTimeout(r, wait));
        }

        if (!isCurrentRequest(parentController)) return;

        setData(mapped);
        callbacksRef.current?.onSuccess?.(mapped);
      } catch (err) {
        // Abort from parent signal: don't show error (we already returned/closed).
        // A timeout also aborts the controller, so the fetch may reject with
        // AbortError before the timeout's own error wins the race; treat that as
        // a timeout (not a silent parent-abort) via the explicit timedOut flag.
        if (isAbortError(err) && !timedOut) {
          return;
        }
        if (!isCurrentRequest(parentController)) return;

        // A specific application error (inactive layout) is authoritative even if the
        // timer fired late: we clearly received and parsed the response, so it wasn't
        // really a network timeout. Check it before the timeout flag.
        const isInactiveError = err instanceof Error && err.message.includes('is not active');
        const isTimeoutError = !isInactiveError && timedOut;
        const error: RoomLayoutError = isInactiveError
          ? { code: RoomLayoutErrorCode.LAYOUT_INACTIVE, message: err.message }
          : isTimeoutError
            ? { code: RoomLayoutErrorCode.CONNECTION_ERROR }
            : errorFromCaught(err);

        setError(error);
        isErrorRef.current = true;
        setIsError(true);
        callbacksRef.current?.onError?.(error);
      } finally {
        if (timeoutId !== null) clearTimeout(timeoutId);
        signal.removeEventListener('abort', onParentAbort);
        if (isCurrentRequest(parentController)) {
          setIsLoading(false);
        }
      }
    },
    [layoutId, envUrl, userToken, timeSlotId, isCurrentRequest]
  );

  /** Start a load, aborting any in-flight request so only the latest request updates state. */
  const startLoad = useCallback(() => {
    currentRequestControllerRef.current?.abort();
    const controller = new AbortController();
    currentRequestControllerRef.current = controller;
    void runLoad(controller);
  }, [runLoad]);

  const refetch = useCallback(() => {
    loadLayoutRef.current?.();
  }, []);

  useEffect(() => {
    if (!hasLayout && !hasTimeSlot) {
      setIsLoading(false);
      setData(null);
      isErrorRef.current = false;
      setIsError(false);
      setError(null);
      loadLayoutRef.current = null;
      currentRequestControllerRef.current = null;
      return;
    }
    if (!hasToken) {
      const authError: RoomLayoutError = { code: RoomLayoutErrorCode.CLIENT_ERROR };
      setIsLoading(false);
      setData(null);
      isErrorRef.current = true;
      setIsError(true);
      setError(authError);
      callbacksRef.current?.onError?.(authError);
      loadLayoutRef.current = null;
      currentRequestControllerRef.current = null;
      return;
    }

    loadLayoutRef.current = startLoad;
    startLoad();

    return () => {
      currentRequestControllerRef.current?.abort();
      currentRequestControllerRef.current = null;
      loadLayoutRef.current = null;
    };
  }, [
    layoutId,
    envUrl,
    userToken,
    hasToken,
    timeSlotId,
    runLoad,
    hasLayout,
    hasTimeSlot,
    startLoad
  ]);

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isErrorRef
  };
}
